import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { usePlanLimits } from '@features/subscription/hooks/usePlanLimits';
import { calculateCronInterval, validateCronInterval } from '@features/backups/utils/cronInterval';

interface UseCronValidationReturn {
  isValid: boolean;
  intervalMinutes: number | null;
  error: string | null;
  minScheduleInterval: number;
}

/**
 * Hook to validate a CRON expression against plan limits
 */
export function useCronValidation(
  cronExpr: string,
  enabled: boolean = true
): UseCronValidationReturn {
  const { planLimits } = usePlanLimits();
  const { t } = useTranslation('backups');

  const minScheduleInterval = planLimits?.minScheduleInterval ?? 60;

  return useMemo(() => {
    // Skip validation if not enabled
    if (!enabled || !cronExpr.trim()) {
      return {
        isValid: true,
        intervalMinutes: null,
        error: null,
        minScheduleInterval,
      };
    }

    // First check if CRON is valid and get interval
    const parseResult = calculateCronInterval(cronExpr);

    if (!parseResult.isValid) {
      return {
        isValid: false,
        intervalMinutes: null,
        error:
          parseResult.error === 'CRON expression is required'
            ? t('schedules.errors.cronRequired')
            : t('schedules.errors.invalidCron'),
        minScheduleInterval,
      };
    }

    // Then validate against plan limits
    const validation = validateCronInterval(cronExpr, minScheduleInterval);

    return {
      isValid: validation.isValid,
      intervalMinutes: parseResult.minutes,
      error: validation.isValid
        ? null
        : t('schedules.errors.cronMinimumInterval', {
            interval: parseResult.minutes,
            minimum: minScheduleInterval,
          }),
      minScheduleInterval,
    };
  }, [cronExpr, enabled, minScheduleInterval, t]);
}

export default useCronValidation;
